import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";
import type { U3141LotDetailOperationTypeFromInitLU } from "../u3141LandRegister/U3141Service";
import type {
  U3140DocumentPdfAnnotationsGetResult,
  U3140DocumentPdfAnnotationsPayload,
  U3140DocumentPdfFileVariant,
} from "../u3140PdfAnnotations/U3140PdfAnnotationsService";
import {
  doGetDocumentPdfFileArrayBuffer as u3140DoGetDocumentPdfFileArrayBuffer,
  doGetDocumentPdfAnnotations as u3140DoGetDocumentPdfAnnotations,
  doPutDocumentPdfAnnotations as u3140DoPutDocumentPdfAnnotations,
  doGetPdfAnnotationToolbarPresets,
  doCreatePdfAnnotationToolbarPreset,
  doUpdatePdfAnnotationToolbarPreset,
  doDeletePdfAnnotationToolbarPreset,
  luHandleAppiError as u3140LuHandleAppiError,
} from "../u3140PdfAnnotations/U3140PdfAnnotationsService";
export { U3142_PDF_ANNOTATION_FILL_NONE } from "../u3140PdfAnnotations/U3140PdfAnnotationsPalette";

/** @deprecated Utiliser les réexportations ; conservé pour compatibilité typage endpoints. */
type U3142InternalPdfVariant = U3140DocumentPdfFileVariant;

const LOGICALUNIT_URL = "U3142";

/** Variante `GET …/pdf-file` : aplat avec annotations (défaut API) ou original sans aplat (éditeur). */

/** Paramètres initLU U3142 (alignés U3141 pour les types d’opération). */
export interface U3142InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  lotDetailOperationTypes: U3141LotDetailOperationTypeFromInitLU[];
}

type U3142EndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  FINDDISTRICTS: string;
  FINDCADASTRES: string;
  FINDSEARCHLOTS: string;
  FINDSEARCHDISTRICTS: string;
  FINDSEARCHCADASTRES: string;
  EXPORT_EXCEL: string;
  GETDOCUMENTS: (id: string | number) => string;
  POSTDOCUMENT: (id: string | number) => string;
  DELETEDOCUMENT: (id: string | number, documentId: string | number) => string;
  /** Mise à jour partielle d'un document du lot (ex. renommage documentName). */
  PATCHDOCUMENT: (id: string | number, documentId: string | number) => string;
  VIEWDOCUMENT: (documentId: string | number) => string;
  GETDOCUMENTPDFFILE: (documentId: string | number, variant?: U3142InternalPdfVariant) => string;
  GETDOCUMENTANNOTATIONS: (documentId: string | number) => string;
  PUTDOCUMENTANNOTATIONS: (documentId: string | number) => string;
  GETPDFANNOTATIONTOOLBARPRESETS: string;
  CREATEPDFANNOTATIONTOOLBARPRESET: string;
  UPDATEPDFANNOTATIONTOOLBARPRESET: (presetId: string | number) => string;
  DELETEPDFANNOTATIONTOOLBARPRESET: (presetId: string | number) => string;
};

const U3142Endpoints: U3142EndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  FINDDISTRICTS: `${LOGICALUNIT_URL}/findDistricts`,
  FINDCADASTRES: `${LOGICALUNIT_URL}/findCadastres`,
  FINDSEARCHLOTS: `${LOGICALUNIT_URL}/findSearchLots`,
  FINDSEARCHDISTRICTS: `${LOGICALUNIT_URL}/findSearchDistricts`,
  FINDSEARCHCADASTRES: `${LOGICALUNIT_URL}/findSearchCadastres`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
  GETDOCUMENTS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/documents`,
  POSTDOCUMENT: (id: string | number) => `${LOGICALUNIT_URL}/${id}/documents`,
  DELETEDOCUMENT: (id: string | number, documentId: string | number) =>
    `${LOGICALUNIT_URL}/${id}/documents/${documentId}`,
  PATCHDOCUMENT: (id: string | number, documentId: string | number) =>
    `${LOGICALUNIT_URL}/${id}/documents/${documentId}`,
  VIEWDOCUMENT: (documentId: string | number) => `${LOGICALUNIT_URL}/document/${documentId}`,
  GETDOCUMENTPDFFILE: (documentId: string | number, variant?: U3142InternalPdfVariant) => {
    const base = `${LOGICALUNIT_URL}/document/${documentId}/pdf-file`;
    if (variant === undefined) {
      return base;
    }
    return `${base}?variant=${variant}`;
  },
  GETDOCUMENTANNOTATIONS: (documentId: string | number) => `${LOGICALUNIT_URL}/document/${documentId}/annotations`,
  PUTDOCUMENTANNOTATIONS: (documentId: string | number) => `${LOGICALUNIT_URL}/document/${documentId}/annotations`,
  GETPDFANNOTATIONTOOLBARPRESETS: `${LOGICALUNIT_URL}/pdf-annotation-toolbar-presets`,
  CREATEPDFANNOTATIONTOOLBARPRESET: `${LOGICALUNIT_URL}/pdf-annotation-toolbar-presets`,
  UPDATEPDFANNOTATIONTOOLBARPRESET: (presetId: string | number) =>
    `${LOGICALUNIT_URL}/pdf-annotation-toolbar-presets/${presetId}`,
  DELETEPDFANNOTATIONTOOLBARPRESET: (presetId: string | number) =>
    `${LOGICALUNIT_URL}/pdf-annotation-toolbar-presets/${presetId}`,
};

export interface U3142District {
  id: number;
  propertyDistrict: string;
  /** Code district (optionnel, pour format concordance 7 parties). */
  codeDistrict?: string;
}

export interface U3142Cadastre {
  id: number;
  cadastre: string;
  rankCode: string;
  rankLabel: string;
  /** Code cadastre (optionnel, pour format concordance 7 parties). */
  cadastreCode?: string;
}

export type U3142AutoCompletCriterias = { fieldName: string; value: string | number }[];

export interface U3142FindSearchLotItem {
  id: string;
  lot?: string;
  propertyDistrict?: string;
  cadastre?: string;
  rankLabel?: string;
  partieDeLot?: boolean;
}

export interface U3142FindSearchDistrictItem {
  id: string;
  codeDistrict?: string;
  propertyDistrict?: string;
}

export interface U3142FindSearchCadastreItem {
  id: string;
  cadastreCode?: string;
  cadastre?: string;
  rankCode?: string;
  rankLabel?: string;
  district?: { id: string; codeDistrict?: string; propertyDistrict?: string };
}

/** Types de document addables dans l'onglet Documents (aligné U3141). */
export const U3142_LOT_DETAIL_DOCUMENT_TYPES = [
  "rfLotDetailIndex",
  "rfLotDetailPlan",
  "rfLotPlansCorrectionCadastrale",
  "rfLotLivreRenvoi",
] as const;

/** Types pour lesquels on peut ajouter plusieurs documents (un par numéro de projet ; le no projet ne doit pas déjà être dans la liste du lot pour ce type). */
export const U3142_MULTI_DOCUMENT_TYPES = [
  "rfLotPlansCorrectionCadastrale",
  "rfLotLivreRenvoi",
] as const;

/** File d’attente dialogue « Ajouter un document » (aligné U3141). */
export const U3142_DOCUMENT_UPLOAD_MAX_FILES = 50;

export type U3142LotDetailDocumentType = (typeof U3142_LOT_DETAIL_DOCUMENT_TYPES)[number];


// --- PDF annotations (unité commune U3140) — types et helpers ---
export type { U3140DocumentLogicalUnit } from "../u3140PdfAnnotations/U3140PdfAnnotationsService";
export type { U3140DocumentPdfFileVariant as U3142DocumentPdfFileVariant } from "../u3140PdfAnnotations/U3140PdfAnnotationsService";
export type {
  U3140PdfAnnotationTextAlign as U3142PdfAnnotationTextAlign,
  U3140PdfAnnotationTextBoxVerticalAlign as U3142PdfAnnotationTextBoxVerticalAlign,
  U3140PdfAnnotationTextVerticalAnchor as U3142PdfAnnotationTextVerticalAnchor,
  U3140PdfAnnotationMetadata as U3142PdfAnnotationMetadata,
  U3140PdfAnnotationShape as U3142PdfAnnotationShape,
  U3140DocumentPdfAnnotationsLotPayload as U3142DocumentPdfAnnotationsLotPayload,
  U3140DocumentPdfAnnotationsPayload as U3142DocumentPdfAnnotationsPayload,
  U3140DocumentPageSegment as U3142DocumentPageSegment,
  U3140DocumentPdfAnnotationsGetResult as U3142DocumentPdfAnnotationsGetResult,
  U3140PdfAnnotationPresetShapeKind as U3142PdfAnnotationPresetShapeKind,
  U3140PdfAnnotationToolbarStylePreset as U3142PdfAnnotationToolbarStylePreset,
  U3140PdfAnnotationToolbarPresetStyles as U3142PdfAnnotationToolbarPresetStyles,
  U3140PdfAnnotationToolbarPreset as U3142PdfAnnotationToolbarPreset,
  U3140PdfAnnotationToolbarPresetCreatePayload as U3142PdfAnnotationToolbarPresetCreatePayload,
  U3140PdfAnnotationToolbarPresetUpdatePayload as U3142PdfAnnotationToolbarPresetUpdatePayload,
} from "../u3140PdfAnnotations/U3140PdfAnnotationsService";
export {
  u3140MergeToolbarPresetStyles as u3142MergeToolbarPresetStyles,
  u3140RelativeLuminanceForHex as u3142RelativeLuminanceForHex,
  u3140ContrastingForegroundHexForBackground as u3142ContrastingForegroundHexForBackground,
  u3140ToolbarPresetSidebarBackgroundHex as u3142ToolbarPresetSidebarBackgroundHex,
} from "../u3140PdfAnnotations/U3140PdfAnnotationsService";

/** Un document du tableau lotDetail.documents. L'API renvoie projectNumber à la racine (GET :id/documents, GET :id). */
export interface U3142LotDetailDocument {
  id: string | number;
  documentName?: string;
  documentType?: string;
  /** Numéro de projet (renvoyé à la racine par l'API lorsqu'il est renseigné en base). */
  projectNumber?: string;
  /** Forme alternative possible (directive) : project.projectNumber. */
  project?: { projectNumber?: string };
}

export interface U3142LotDetail {
  id: string;
  lot: string;
  /** Partie de lot (suffixe (ptie) dans les chaînes de concordance). Obligatoire à la création (API). */
  partieDeLot?: boolean;
  dateEstablishment?: Date | string | null;
  concordance?: string[] | null;
  lotState: string;
  district?: U3142District | null;
  cadastre?: U3142Cadastre | null;
  operationType?: string | null;
  /** Texte libre complémentaire sur le type d’opération (API : operation_type_info). */
  operationTypeInfo?: string | null;
  /** Miroir GET (optionnel) : indique si le complément est prévu pour le type courant (UX). */
  operationTypeInfoEnabled?: boolean;
  corrections?: string[] | null;
  /** Tableau 0..n de documents (backend). */
  documents?: U3142LotDetailDocument[];
  concordanceOrigines?: string[];
  childLotChains?: any[];
  concordanceLots?: U3142LotDetail[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
  deletedAt?: Date | string | null;
}

export const emptyLotDetail: U3142LotDetail = {
  id: "",
  lot: "",
  partieDeLot: false,
  dateEstablishment: null,
  concordance: null,
  lotState: "",
  district: null,
  cadastre: null,
  operationType: null,
  operationTypeInfo: null,
  corrections: null,
  documents: [],
  concordanceOrigines: [],
  childLotChains: [],
  concordanceLots: [],
};

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(U3142Endpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(U3142Endpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(U3142Endpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, data: U3142LotDetail) => {
  return axios.post(U3142Endpoints.CREATE, data);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, data: U3142LotDetail) => {
  return axios.post(U3142Endpoints.UPDATE(id), data);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, data?: U3142LotDetail) => {
  return axios.post(U3142Endpoints.DELETE(id), data);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(U3142Endpoints.AUDITS(id), { params });
};

export const doFindDistricts = (axios: AxiosInstance, criterias: { fieldName: string; value: any }[]) => {
  return axios.post(U3142Endpoints.FINDDISTRICTS, criterias);
};

export const doFindCadastres = (axios: AxiosInstance, criterias: { fieldName: string; value: any }[]) => {
  return axios.post(U3142Endpoints.FINDCADASTRES, criterias);
};

/** Autocomplete critère liste — lots (BL-0067, distinct du formulaire). */
export const doFindSearchLots = (axios: AxiosInstance, criterias: U3142AutoCompletCriterias) => {
  return axios.post(U3142Endpoints.FINDSEARCHLOTS, criterias);
};

/** Autocomplete critère liste — districts (BL-0067). */
export const doFindSearchDistricts = (axios: AxiosInstance, criterias: U3142AutoCompletCriterias) => {
  return axios.post(U3142Endpoints.FINDSEARCHDISTRICTS, criterias);
};

/** Autocomplete critère liste — cadastres globaux avec district (BL-0067). */
export const doFindSearchCadastres = (axios: AxiosInstance, criterias: U3142AutoCompletCriterias) => {
  return axios.post(U3142Endpoints.FINDSEARCHCADASTRES, criterias);
};

// API Calls - Export Excel
export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") => {
  return axios.post(U3142Endpoints.EXPORT_EXCEL, { filter, lang });
};

// API Calls - Documents (onglet Documents)

/** Réponse GET document (signUrl + projectNumber si présent). */
export interface U3142ViewDocument {
  signUrl: string;
  projectNumber?: string;
}

/** GET liste des documents du lot. Réponse : tableau ou wrapper result/results/data. */
export const doGetDocumentsList = (axios: AxiosInstance, lotId: string | number): Promise<U3142LotDetailDocument[]> => {
  return axios.get(U3142Endpoints.GETDOCUMENTS(lotId)).then((res) => {
    const raw = res.data;
    if (Array.isArray(raw)) return raw;
    if (raw?.results && Array.isArray(raw.results)) return raw.results;
    if (raw?.result && Array.isArray(raw.result)) return raw.result;
    if (raw?.data && Array.isArray(raw.data)) return raw.data;
    return [];
  });
};

/** GET document (URL pré-signée + projectNumber si présent). */
export const doViewDocument = (axios: AxiosInstance, documentId: string | number) => {
  return axios.get<U3142ViewDocument>(U3142Endpoints.VIEWDOCUMENT(documentId));
};

/**
 * GET fichier document (PDF binaire via API, même origine — évite CORS S3 pour PDF.js).
 * Délègue à U3140 avec préfixe `U3142`.
 * @param variant — `original` : fond vierge pour l’édition d’annotations ; `annotated` ou omis : PDF aplat (lecture, défaut backend).
 */
export const doGetDocumentPdfFileArrayBuffer = (
  axios: AxiosInstance,
  documentId: string | number,
  variant?: U3140DocumentPdfFileVariant
): Promise<ArrayBuffer> => {
  return u3140DoGetDocumentPdfFileArrayBuffer(axios, documentId, variant, "U3142");
};

/** POST création document (multipart: documentType, file, projectNumber). */
export const doCreateDocument = (axios: AxiosInstance, lotId: string | number, formData: FormData) => {
  return axios.post(U3142Endpoints.POSTDOCUMENT(lotId), formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/** DELETE document (supprime lien document–lot puis soft-delete). */
export const doDeleteDocument = (
  axios: AxiosInstance,
  lotId: string | number,
  documentId: string | number
) => {
  return axios.delete(U3142Endpoints.DELETEDOCUMENT(lotId, documentId));
};

/** PATCH document du lot (corps JSON : documentName). */
export const doPatchDocumentName = (
  axios: AxiosInstance,
  lotId: string | number,
  documentId: string | number,
  documentName: string
) => {
  return axios.patch(U3142Endpoints.PATCHDOCUMENT(lotId, documentId), { documentName });
};

/** GET annotations JSON — délègue à U3140 (`U3142`). */
export const doGetDocumentPdfAnnotations = (
  axios: AxiosInstance,
  documentId: string | number
): Promise<U3140DocumentPdfAnnotationsGetResult> =>
  u3140DoGetDocumentPdfAnnotations(axios, documentId, "U3142");

/** PUT annotations — délègue à U3140 (`U3142`). */
export const doPutDocumentPdfAnnotations = (
  axios: AxiosInstance,
  documentId: string | number,
  payload: U3140DocumentPdfAnnotationsPayload
) => u3140DoPutDocumentPdfAnnotations(axios, documentId, payload, "U3142");

export {
  doGetPdfAnnotationToolbarPresets,
  doCreatePdfAnnotationToolbarPreset,
  doUpdatePdfAnnotationToolbarPreset,
  doDeletePdfAnnotationToolbarPreset,
};

export function luHandleAppiError(
  t: TranslateFn,
  NS: string,
  err: unknown,
  errorResult: U2000ApiErrorResult
): void {
  u3140LuHandleAppiError(t, NS, err, errorResult);
}
